/**
 * Both time axes, said out loud whenever either is not the default.
 *
 * Reading a reconstructed past state and mistaking it for the present is the failure this
 * prevents. The server marks the response `historical`, so the client does not have to work it
 * out — and `asAt` at anything other than today is worth saying even when `recordedAt` is now.
 */

import { Callout } from '~/ui/Feedback';
import { fmtDate, fmtInstant } from '~/lib/format';

export interface TemporalBannerProps {
  asAt: string;
  recordedAt: string;
  historical: boolean;
  /** Today in the register's own timezone, where the screen knows it. */
  today?: string;
}

export function TemporalBanner({ asAt, recordedAt, historical, today }: TemporalBannerProps) {
  const pastValidTime = today !== undefined && asAt !== today;
  if (!historical && !pastValidTime) return null;

  return (
    <Callout tone="quest" title="This is not the present">
      {historical ? (
        <p>
          Showing the law as at {fmtDate(asAt)}, as the engine recorded it on{' '}
          {fmtInstant(recordedAt)}. This is a reconstruction of what the engine believed at that
          instant, not what it holds now.
        </p>
      ) : (
        <p>
          Showing the law as at {fmtDate(asAt)}, as the engine holds it today. A rule that has
          since changed will read as it stood on that date.
        </p>
      )}
    </Callout>
  );
}

/** The two controls that set the axes, for a screen that offers them. */
export interface TemporalControlsProps {
  asAt: string;
  recordedAt: string;
  onChange: (patch: { asAt?: string; recordedAt?: string }) => void;
}
